/**
 * NewPresentationDialog — crea un deck performance: titolo, periodo (trimestre
 * predefinito o mesi consecutivi liberi), selezione negozi (default: tutti i visibili).
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Presentation, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const MONTHS_SHORT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const QUARTERS = [
  { key: 'q1', label: 'Q1 · Gen–Mar', months: [1, 2, 3] },
  { key: 'q2', label: 'Q2 · Apr–Giu', months: [4, 5, 6] },
  { key: 'q3', label: 'Q3 · Lug–Set', months: [7, 8, 9] },
  { key: 'q4', label: 'Q4 · Ott–Dic', months: [10, 11, 12] },
];

const shiftMonth = (m, y, d) => {
  const i = y * 12 + (m - 1) + d;
  return [(i % 12) + 1, Math.floor(i / 12)];
};

const rangeLabel = (periods) => {
  if (periods.length === 0) return '';
  const [m1, y1] = periods[0];
  const [m2, y2] = periods[periods.length - 1];
  if (periods.length === 1) return `${MONTHS_FULL[m1 - 1]} ${y1}`;
  if (y1 === y2) return `${MONTHS_SHORT[m1 - 1]}–${MONTHS_SHORT[m2 - 1]} ${y1}`;
  return `${MONTHS_SHORT[m1 - 1]} ${y1}–${MONTHS_SHORT[m2 - 1]} ${y2}`;
};

export default function NewPresentationDialog({ open, onClose, onCreated }) {
  const now = new Date();
  // default: ultimo trimestre concluso
  const curQ = Math.floor(now.getMonth() / 3);
  const defaultQ = curQ === 0 ? 3 : curQ - 1;
  const defaultYear = curQ === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [title, setTitle] = useState('');
  const [months, setMonths] = useState(new Set(QUARTERS[defaultQ].months));
  const [year, setYear] = useState(String(defaultYear));
  const [stores, setStores] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [brandFilter, setBrandFilter] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setBrandFilter(new Set());
    api.get('/stores').then(r => {
      const list = (r.data || []).sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'it'));
      setStores(list);
      setSel(new Set(list.map(s => s.store_id)));
    }).catch(() => {});
  }, [open]);

  const allSelected = useMemo(() => stores.length > 0 && sel.size === stores.length, [stores, sel]);

  const sortedMonths = useMemo(() => [...months].sort((a, b) => a - b), [months]);
  const contiguous = sortedMonths.every((m, i) => i === 0 || m === sortedMonths[i - 1] + 1);
  const yearNum = parseInt(year, 10);
  const periodValid = sortedMonths.length > 0 && contiguous;
  const curPeriods = sortedMonths.map(m => [m, yearNum]);
  const prevPeriods = sortedMonths.map((_, i) => shiftMonth(sortedMonths[0], yearNum, -(sortedMonths.length - i)));
  const lyPeriods = sortedMonths.map(m => [m, yearNum - 1]);

  const toggleMonth = (m) => setMonths(prev => {
    const next = new Set(prev);
    if (next.has(m)) next.delete(m); else next.add(m);
    return next;
  });

  // Gruppi Brand/Area derivati dagli store visibili: click su un chip = toggle
  // di tutti i negozi del gruppo. Il brand attivo pre-filtra i chip Aree.
  const brandGroups = useMemo(() => {
    const map = new Map();
    for (const s of stores) {
      const name = s.brand_name || 'Senza brand';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(s.store_id);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'it'));
  }, [stores]);

  const areaGroups = useMemo(() => {
    const map = new Map();
    const pool = brandFilter.size > 0
      ? stores.filter(s => brandFilter.has(s.brand_name || 'Senza brand'))
      : stores;
    for (const s of pool) {
      const name = s.area || 'Senza area';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(s.store_id);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'it'));
  }, [stores, brandFilter]);

  const toggle = (id) => setSel(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGroup = (ids) => setSel(prev => {
    const next = new Set(prev);
    const allIn = ids.every(id => next.has(id));
    ids.forEach(id => allIn ? next.delete(id) : next.add(id));
    return next;
  });

  const toggleBrand = (name, ids) => {
    const activating = !brandFilter.has(name);
    setBrandFilter(prev => {
      const next = new Set(prev);
      if (activating) next.add(name); else next.delete(name);
      return next;
    });
    setSel(prev => {
      const next = new Set(prev);
      ids.forEach(id => activating ? next.add(id) : next.delete(id));
      return next;
    });
  };

  const GroupChips = ({ label, groups, prefix, isActive, onToggle }) => groups.length > 1 && (
    <div className="mb-2">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 block mb-1">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {groups.map(([name, ids]) => {
          const active = isActive(name, ids);
          return (
            <button key={name} type="button" onClick={() => onToggle(name, ids)}
                    data-testid={`presentation-${prefix}-chip-${name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`text-xs px-2.5 py-1 border transition-colors ${active
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}>
              {name} <span className={active ? 'text-slate-300' : 'text-slate-400'}>({ids.length})</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const submit = async () => {
    if (sel.size === 0) { toast.error('Seleziona almeno un negozio'); return; }
    if (!periodValid) { toast.error('Seleziona mesi consecutivi (es. Feb–Apr)'); return; }
    setSaving(true);
    try {
      await api.post('/presentations', {
        title: title.trim() || null,
        months: sortedMonths,
        year: yearNum,
        store_ids: [...sel],
      });
      toast.success('Presentazione creata!');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Creazione fallita');
    } finally {
      setSaving(false);
    }
  };

  const years = [defaultYear + 1, defaultYear, defaultYear - 1, defaultYear - 2].map(String);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="new-presentation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Presentation className="w-5 h-5" /> Nuova presentazione</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
            KPI del periodo (trimestre o mesi consecutivi), confronto col periodo precedente e con l'anno scorso, ranking e analisi AI.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto pr-1">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Titolo (opzionale)</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={120}
                   placeholder="Es. Performance Area Nord — Q1"
                   className="rounded-none" data-testid="presentation-title-input" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-3 mb-1">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Periodo *</label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="rounded-none h-7 w-24 text-xs" data-testid="presentation-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {QUARTERS.map(q => {
                const active = sortedMonths.length === 3 && q.months.every(m => months.has(m));
                return (
                  <button key={q.key} type="button" onClick={() => setMonths(new Set(q.months))}
                          data-testid={`presentation-quarter-${q.key}`}
                          className={`text-xs px-2.5 py-1 border font-medium transition-colors ${active
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>
                    {q.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-6 gap-1" data-testid="presentation-months-grid">
              {MONTHS_SHORT.map((m, i) => {
                const active = months.has(i + 1);
                return (
                  <button key={m} type="button" onClick={() => toggleMonth(i + 1)}
                          data-testid={`presentation-month-chip-${i + 1}`}
                          className={`text-xs py-1.5 border transition-colors ${active
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}>
                    {m}
                  </button>
                );
              })}
            </div>
            {periodValid ? (
              <p className="text-[11px] text-slate-500 mt-1.5" data-testid="presentation-period-hint">
                <strong className="text-slate-700">{rangeLabel(curPeriods)}</strong> · confronto vs {rangeLabel(prevPeriods)} e vs {rangeLabel(lyPeriods)}
              </p>
            ) : (
              <p className="text-[11px] text-red-600 mt-1.5" data-testid="presentation-months-error">
                {sortedMonths.length === 0 ? 'Seleziona almeno un mese.' : 'I mesi devono essere consecutivi (es. Feb–Apr).'}
              </p>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                Negozi * ({sel.size}/{stores.length})
              </label>
              <button type="button" onClick={() => setSel(allSelected ? new Set() : new Set(stores.map(s => s.store_id)))}
                      className="text-xs text-blue-700 hover:underline" data-testid="presentation-toggle-all-stores">
                {allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </button>
            </div>
            <GroupChips label="Brand" groups={brandGroups} prefix="brand"
                        isActive={(name) => brandFilter.has(name)} onToggle={toggleBrand} />
            <GroupChips label={brandFilter.size > 0 ? 'Aree (filtrate per brand)' : 'Aree'} groups={areaGroups} prefix="area"
                        isActive={(_, ids) => ids.every(id => sel.has(id))} onToggle={(_, ids) => toggleGroup(ids)} />
            <div className="border border-slate-200 divide-y divide-slate-100 max-h-52 overflow-y-auto" data-testid="presentation-stores-list">
              {stores.map(s => (
                <label key={s.store_id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={sel.has(s.store_id)} onChange={() => toggle(s.store_id)}
                         className="w-4 h-4 accent-slate-900" data-testid={`presentation-store-${s.store_id}`} />
                  <span className="text-sm text-slate-700">{s.display_name || s.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={saving || sel.size === 0 || !periodValid} className="gap-1.5" data-testid="presentation-create-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Genero il deck…</> : 'Crea presentazione'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
